import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Interface para os filtros de tickets
 */
export interface TicketFilters {
  cliente_id?: number;
  status?: string[];
  prioridade?: string[];
  apenas_meus?: boolean;
  busca?: string;
}

/**
 * Interface para cliente
 */
interface Cliente {
  id: number;
  company_name: string;
  trading_name?: string;
}

/**
 * Props do componente TicketFilterBar
 */
interface TicketFilterBarProps {
  onFilterChange: (filters: TicketFilters) => void;
  initialFilters?: TicketFilters;
  clientesList: Cliente[];
  isLoading?: boolean;
  totalTickets?: number;
  filteredTickets?: number;
}

/**
 * Opções de status disponíveis
 */
const STATUS_OPTIONS = [
  { value: 'open', label: 'Aberto', color: 'bg-blue-500' },
  { value: 'assigned', label: 'Atribuído', color: 'bg-purple-500' },
  { value: 'in_progress', label: 'Em Progresso', color: 'bg-yellow-500' },
  { value: 'waiting_client', label: 'Aguardando Cliente', color: 'bg-orange-500' },
  { value: 'waiting_internal', label: 'Aguardando Interno', color: 'bg-cyan-500' },
  { value: 'resolved', label: 'Resolvido', color: 'bg-green-500' },
  { value: 'closed', label: 'Fechado', color: 'bg-gray-500' },
  { value: 'cancelled', label: 'Cancelado', color: 'bg-red-500' },
];

/**
 * Opções de prioridade disponíveis
 */
const PRIORIDADE_OPTIONS = [
  { value: 'low', label: 'Baixa', color: 'bg-gray-400' },
  { value: 'medium', label: 'Média', color: 'bg-blue-400' },
  { value: 'high', label: 'Alta', color: 'bg-orange-400' },
  { value: 'urgent', label: 'Urgente', color: 'bg-red-500' },
];

/**
 * Componente de barra de filtros para tickets
 * 
 * Requisitos: 2.1.2, 2.5.3
 */
export function TicketFilterBar({
  onFilterChange,
  initialFilters = {},
  clientesList,
  isLoading = false,
  totalTickets = 0,
  filteredTickets = 0,
}: TicketFilterBarProps) {
  const [filters, setFilters] = useState<TicketFilters>(initialFilters);
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchInput, setSearchInput] = useState(initialFilters.busca || '');

  // Remover useEffect problemático que causa loop infinito
  // A sincronização com initialFilters não é necessária pois o estado já é inicializado com ele

  /**
   * Atualiza filtro de cliente
   */
  const handleClienteChange = (clienteId: string) => {
    const newFilters = {
      ...filters,
      cliente_id: clienteId === 'all' ? undefined : parseInt(clienteId),
    };
    setFilters(newFilters);
  };

  /**
   * Toggle checkbox de status
   */
  const handleStatusToggle = (status: string) => {
    const currentStatus = filters.status || [];
    const newStatus = currentStatus.includes(status)
      ? currentStatus.filter((s) => s !== status)
      : [...currentStatus, status];

    const newFilters = {
      ...filters,
      status: newStatus.length > 0 ? newStatus : undefined,
    };
    setFilters(newFilters);
  };

  /**
   * Toggle checkbox de prioridade
   */
  const handlePrioridadeToggle = (prioridade: string) => {
    const currentPrioridade = filters.prioridade || [];
    const newPrioridade = currentPrioridade.includes(prioridade)
      ? currentPrioridade.filter((p) => p !== prioridade)
      : [...currentPrioridade, prioridade];

    const newFilters = {
      ...filters,
      prioridade: newPrioridade.length > 0 ? newPrioridade : undefined,
    };
    setFilters(newFilters);
  };

  /**
   * Toggle checkbox "Apenas meus tickets"
   */
  const handleApenasMeusToggle = (checked: boolean) => {
    const newFilters = {
      ...filters,
      apenas_meus: checked || undefined,
    };
    setFilters(newFilters);
  };

  /**
   * Atualiza campo de busca
   */
  const handleBuscaChange = (texto: string) => {
    setSearchInput(texto);
  };

  /**
   * Aplica os filtros
   */
  const handleAplicarFiltros = () => {
    const finalFilters = {
      ...filters,
      busca: searchInput.trim() || undefined,
    };
    onFilterChange(finalFilters);
  };

  /**
   * Limpa todos os filtros
   */
  const handleLimparFiltros = () => {
    const emptyFilters: TicketFilters = {};
    setFilters(emptyFilters);
    setSearchInput('');
    onFilterChange(emptyFilters);
  };

  /**
   * Conta quantos filtros estão ativos
   */
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.cliente_id) count++;
    if (filters.status && filters.status.length > 0) count++;
    if (filters.prioridade && filters.prioridade.length > 0) count++;
    if (filters.apenas_meus) count++;
    if (filters.busca) count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      {/* Header da barra de filtros */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Filter className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Filtros</h3>
          {activeFiltersCount > 0 && (
            <Badge variant="secondary" className="ml-2">
              {activeFiltersCount} {activeFiltersCount === 1 ? 'filtro' : 'filtros'}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Contador de tickets */}
          {totalTickets > 0 && (
            <span className="text-sm text-gray-600">
              {filteredTickets} de {totalTickets} tickets
            </span>
          )}

          {/* Botão expandir/recolher */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="ml-2"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-4 w-4 mr-1" />
                Recolher
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-1" />
                Expandir
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Busca rápida (sempre visível) */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Buscar por número, assunto ou descrição..."
            value={searchInput}
            onChange={(e) => handleBuscaChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleAplicarFiltros();
              }
            }}
            className="pl-10"
            disabled={isLoading}
          />
        </div>
      </div>

      {/* Filtros expandidos */}
      {isExpanded && (
        <div className="space-y-4 pt-4 border-t border-gray-200">
          {/* Filtro por Cliente */}
          <div>
            <Label htmlFor="cliente-filter" className="mb-2 block">
              Cliente
            </Label>
            <Select
              value={filters.cliente_id?.toString() || 'all'}
              onValueChange={handleClienteChange}
              disabled={isLoading}
            >
              <SelectTrigger id="cliente-filter">
                <SelectValue placeholder="Todos os clientes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os clientes</SelectItem>
                {clientesList.map((cliente) => (
                  <SelectItem key={cliente.id} value={cliente.id.toString()}>
                    {cliente.trading_name || cliente.company_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Filtro por Status */}
          <div>
            <Label className="mb-2 block">Status</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {STATUS_OPTIONS.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`status-${option.value}`}
                    checked={filters.status?.includes(option.value) || false}
                    onCheckedChange={() => handleStatusToggle(option.value)}
                    disabled={isLoading}
                  />
                  <label
                    htmlFor={`status-${option.value}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${option.color}`} />
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Filtro por Prioridade */}
          <div>
            <Label className="mb-2 block">Prioridade</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {PRIORIDADE_OPTIONS.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`prioridade-${option.value}`}
                    checked={filters.prioridade?.includes(option.value) || false}
                    onCheckedChange={() => handlePrioridadeToggle(option.value)}
                    disabled={isLoading}
                  />
                  <label
                    htmlFor={`prioridade-${option.value}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${option.color}`} />
                      {option.label}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Checkbox "Apenas meus tickets" */}
          <div className="flex items-center space-x-2 pt-2">
            <Checkbox
              id="apenas-meus"
              checked={filters.apenas_meus || false}
              onCheckedChange={handleApenasMeusToggle}
              disabled={isLoading}
            />
            <label
              htmlFor="apenas-meus"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              Apenas meus tickets
            </label>
          </div>

          {/* Botões de ação */}
          <div className="flex gap-2 pt-4 border-t border-gray-200">
            <Button
              onClick={handleAplicarFiltros}
              disabled={isLoading}
              className="flex-1"
            >
              <Filter className="h-4 w-4 mr-2" />
              Aplicar Filtros
            </Button>
            <Button
              variant="outline"
              onClick={handleLimparFiltros}
              disabled={isLoading || activeFiltersCount === 0}
            >
              <X className="h-4 w-4 mr-2" />
              Limpar
            </Button>
          </div>
        </div>
      )}

      {/* Filtros ativos (badges) */}
      {!isExpanded && activeFiltersCount > 0 && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200">
          {filters.cliente_id && (
            <Badge variant="secondary">
              Cliente:{' '}
              {clientesList.find((c) => c.id === filters.cliente_id)?.company_name ||
                'Desconhecido'}
            </Badge>
          )}
          {filters.status && filters.status.length > 0 && (
            <Badge variant="secondary">
              Status: {filters.status.length}{' '}
              {filters.status.length === 1 ? 'selecionado' : 'selecionados'}
            </Badge>
          )}
          {filters.prioridade && filters.prioridade.length > 0 && (
            <Badge variant="secondary">
              Prioridade: {filters.prioridade.length}{' '}
              {filters.prioridade.length === 1 ? 'selecionada' : 'selecionadas'}
            </Badge>
          )}
          {filters.apenas_meus && <Badge variant="secondary">Apenas meus tickets</Badge>}
          {filters.busca && <Badge variant="secondary">Busca: "{filters.busca}"</Badge>}
        </div>
      )}
    </div>
  );
}
